/**
 * Entity 67 — Partygoers.
 *
 * Lore: tall bipeds with smooth leathery skin, "typically bright yellow"
 * though red, blue, green and white have been logged; thick blocky legs; a
 * chest carapace; long noodle-like arms; and a carved smiley face. They are
 * intelligent, they hunt in packs, and everything about the party is a lure
 * they built.
 *
 * Two deliberate departures from the page. The wiki's smiley is "smeared with
 * blood" — here it is a dry cut in the face and nothing more, because this
 * game does its work with the building rather than with gore. And the arms end
 * in blunt suckers rather than the documented rings of hooked teeth, for the
 * same reason: from three metres away in a lit room, a mouth on the end of an
 * arm is enough.
 *
 * What matters most is that it is not funny. It is dressed for the party, it
 * is still keeping time to music that stopped a long time ago, and it is
 * keeping time at a tempo that has nothing to do with the speed it is walking
 * at. A figure enjoying itself this much, this far after the fact, is the
 * whole of the horror. No clown, no props, no jokes.
 */

import * as THREE from 'three';
import { clamp, clamp01, damp, TAU, angleDelta, renderYaw } from '../core/math';
import type { EntityView } from './api';
import { bone, castShadows, chain, dampAngle, freeAll, Kit, planarSpeed, type Disposable } from './entkit';

const HIP_Y = 0.915;
const THIGH = 0.44;
const SHIN = 0.4;
/** Hips to shoulders. */
const TORSO = 0.56;
/** Four of these. Long, even, and with no elbow anywhere in them. */
const ARM_BONE = 0.27;
const ARM_BONES = 4;

/** Party tempo, in cycles per second. Roughly 114bpm, and never anything else. */
const TEMPO = 1.9;

/**
 * The rig above builds to 1.88 m at the top of the head, which is a person.
 * Entity 67 is 6.3–7.7 ft — it is supposed to be looking down at you — and
 * `ENTITY_DEFS.partygoer.height` says 2.1, so the model is scaled to agree with
 * both. Per instance, because a crowd of identical heights reads as a prop.
 *
 * The top of the band is not arbitrary: `world/gen/funrooms.ts` heads its low
 * tunnels at 2.15 m, so the tallest Partygoer follows you down one with two
 * centimetres to spare and the shortest still fills it. Raising this past 1.144
 * puts heads through the ceiling of the level they live on.
 */
const HEIGHT_SCALE_MIN = 1.06;
const HEIGHT_SCALE_MAX = 1.14;

/**
 * Skin and carapace, in the order the archives list them. Yellow is the one
 * anyone has actually seen; the others exist so that a second Partygoer is not
 * a copy of the first.
 *
 * The yellow is a stop deeper than the Funrooms' walls on purpose. Canon says
 * bright, but a bright yellow figure against bright yellow wallpaper is a
 * figure the player finds out about by being hit.
 */
const HIDES: ReadonlyArray<readonly [number, number]> = [
  [0xc9971a, 0x9c710f],
  [0xa8322a, 0x7d221c],
  [0xc9c2b4, 0x9a9384],
];

interface PartygoerRig {
  root: THREE.Group;
  hips: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  legs: THREE.Group[][];
  arms: THREE.Group[][];
}

interface PartygoerKit extends Disposable {
  make(hide: number): PartygoerRig;
}

/**
 * The mouth of the `=)`, cut into the face.
 *
 * Deliberately not symmetrical and not centred. A clean even grin on a round
 * yellow head is an emoji, and an emoji is funny; a crooked one that runs
 * further up on one side than the other is a cut somebody made in something,
 * which is not. Every value here is off by a millimetre or two on purpose.
 */
function makeSmileGeometry(): THREE.BufferGeometry {
  const s = new THREE.Shape();
  s.moveTo(-0.055, 0.016);
  s.quadraticCurveTo(-0.004, -0.058, 0.049, -0.006);
  s.quadraticCurveTo(0.0, -0.042, -0.055, 0.016);
  return new THREE.ShapeGeometry(s, 10);
}

/**
 * A sphere with its front planed off, so the face is a flat facet with a hard
 * edge round it. The flat is what makes the cut marking read as carved into
 * something rather than painted onto it, and the long skull keeps it from
 * being a ball with a smile on it.
 */
function makeHeadGeometry(): THREE.BufferGeometry {
  const geo = new THREE.SphereGeometry(0.125, 11, 8);
  geo.scale(0.9, 1.18, 1);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    if (pos.getZ(i) < -0.082) pos.setZ(i, -0.082);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

function buildPartygoerKit(): PartygoerKit {
  const hides = HIDES.map(([skin, shell]) => ({
    skin: new THREE.MeshLambertMaterial({ color: skin }),
    shell: new THREE.MeshLambertMaterial({ color: shell }),
  }));
  // Dried, not bright. Whatever colour this was for the party, it was a long
  // time ago and it has been worn ever since.
  const cloth = new THREE.MeshLambertMaterial({ color: 0x431520 });
  const tieMat = new THREE.MeshLambertMaterial({ color: 0x20242e });
  // Unlit, so a torch beam never lifts it: the cuts stay holes.
  const cut = new THREE.MeshBasicMaterial({ color: 0x08080a, side: THREE.DoubleSide, fog: true });

  const bellyGeo = new THREE.CapsuleGeometry(0.155, 0.3, 4, 8);
  bellyGeo.scale(1, 1, 0.82);
  bellyGeo.translate(0, 0.24, 0);
  // The carapace: a broad shallow shell across the chest, wider than the body
  // under it, with the shoulders squared off on top of it.
  const chestGeo = new THREE.SphereGeometry(0.19, 9, 6);
  chestGeo.scale(1.28, 0.95, 0.72);
  chestGeo.translate(0, 0.47, -0.012);
  const vestGeo = new THREE.SphereGeometry(0.192, 9, 6);
  vestGeo.scale(1.3, 0.99, 0.76);
  vestGeo.translate(0, 0.44, -0.012);
  const tieGeo = new THREE.BoxGeometry(0.05, 0.24, 0.012);
  tieGeo.translate(0, 0.3, -0.155);
  const neckGeo = new THREE.CylinderGeometry(0.05, 0.062, 0.1, 6);
  neckGeo.translate(0, 0.6, 0);
  const headGeo = makeHeadGeometry();
  const smileGeo = makeSmileGeometry();
  const eyeGeo = new THREE.PlaneGeometry(0.042, 0.009);

  // Blocky legs, straight out of the description. They are the heaviest thing
  // on it and they make the bounce read from across a room.
  const thighGeo = new THREE.BoxGeometry(0.165, THIGH, 0.185);
  thighGeo.translate(0, -THIGH / 2, 0);
  const shinGeo = new THREE.BoxGeometry(0.145, SHIN, 0.16);
  shinGeo.translate(0, -SHIN / 2, 0);
  const footGeo = new THREE.BoxGeometry(0.15, 0.055, 0.25);
  footGeo.translate(0, -SHIN - 0.028, -0.04);

  const armGeos: THREE.BufferGeometry[] = [];
  for (let i = 0; i < ARM_BONES; i++) armGeos.push(bone(ARM_BONE, 0.042 - i * 0.005, 0.037 - i * 0.005, 6));
  const cupGeo = new THREE.CylinderGeometry(0.048, 0.03, 0.06, 8, 1, true);
  cupGeo.translate(0, -ARM_BONE - 0.03, 0);
  const mawGeo = new THREE.CircleGeometry(0.042, 8);
  mawGeo.rotateX(Math.PI / 2);
  mawGeo.translate(0, -ARM_BONE - 0.058, 0);

  const geos = [
    bellyGeo,
    chestGeo,
    vestGeo,
    tieGeo,
    neckGeo,
    headGeo,
    smileGeo,
    eyeGeo,
    thighGeo,
    shinGeo,
    footGeo,
    ...armGeos,
    cupGeo,
    mawGeo,
  ];
  const armLens = new Array<number>(ARM_BONES).fill(ARM_BONE);

  return {
    make(hide: number): PartygoerRig {
      const { skin, shell } = hides[hide % hides.length]!;
      const root = new THREE.Group();

      const hips = new THREE.Group();
      hips.position.y = HIP_Y;
      root.add(hips);

      const torso = new THREE.Group();
      hips.add(torso);
      torso.add(
        new THREE.Mesh(bellyGeo, skin),
        new THREE.Mesh(chestGeo, shell),
        new THREE.Mesh(vestGeo, cloth),
        new THREE.Mesh(tieGeo, tieMat),
        new THREE.Mesh(neckGeo, skin)
      );

      const head = new THREE.Group();
      head.position.y = 0.74;
      head.add(new THREE.Mesh(headGeo, skin));
      const smile = new THREE.Mesh(smileGeo, cut);
      smile.position.set(0.004, -0.014, -0.0835);
      smile.rotation.z = 0.06;
      head.add(smile);
      // The two dashes do not match each other either: different lengths,
      // different heights, one of them tilted.
      for (const side of [-1, 1] as const) {
        const eye = new THREE.Mesh(eyeGeo, cut);
        eye.position.set(side * 0.041 + 0.003, side < 0 ? 0.044 : 0.036, -0.0835);
        eye.rotation.z = side < 0 ? 0.17 : -0.04;
        eye.scale.set(side < 0 ? 1 : 0.76, 1, 1);
        head.add(eye);
      }
      torso.add(head);

      const legs: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain([thighGeo, shinGeo], [THIGH, SHIN], skin);
        joints[0]!.position.set(side * 0.115, HIP_Y, 0);
        joints[1]!.add(new THREE.Mesh(footGeo, shell));
        root.add(joints[0]!);
        legs.push(joints);
      }

      const arms: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain(armGeos, armLens, skin);
        joints[0]!.position.set(side * 0.24, 0.48, 0);
        const tip = joints[ARM_BONES - 1]!;
        tip.add(new THREE.Mesh(cupGeo, shell), new THREE.Mesh(mawGeo, cut));
        torso.add(joints[0]!);
        arms.push(joints);
      }

      castShadows(root);
      return { root, hips, torso, head, legs, arms };
    },
    dispose(): void {
      freeAll(geos);
      for (const h of hides) freeAll([h.skin, h.shell]);
      freeAll([cloth, tieMat, cut]);
    },
  };
}

const partygoerKit = new Kit(buildPartygoerKit);

/**
 * Build a Partygoer. Yellow, dressed, and still going.
 */
export function createPartygoerView(): EntityView {
  const kit = partygoerKit.acquire();
  // Yellow most of the time; the archives log the others as rare and so are
  // they. Cosmetic, per instance.
  const rig = kit.make(Math.random() < 0.72 ? 0 : 1 + ((Math.random() * 2) | 0));
  const object = new THREE.Group();
  object.add(rig.root);
  const tall = HEIGHT_SCALE_MIN + Math.random() * (HEIGHT_SCALE_MAX - HEIGHT_SCALE_MIN);
  rig.root.scale.setScalar(tall);

  const phase = Math.random() * TAU;
  let stride = phase;
  /** The tempo it is keeping. Runs on the clock, not on the legs. */
  let beat = phase;
  let yaw = 0;
  let yawInit = false;
  let headYaw = 0;
  let lunge = 0;
  let dead = 0;

  return {
    object,
    update(state, dt, _time, camera) {
      if (!yawInit) {
        yaw = state.yaw;
        yawInit = true;
      }
      yaw = dampAngle(yaw, state.yaw, 5.5, dt);
      object.position.set(state.pos.x, 0, state.pos.z);
      object.rotation.y = renderYaw(yaw);

      const speed = planarSpeed(state);
      const drive = clamp01(speed / 4.4);
      stride += dt * (0.9 + speed * 2.4);
      lunge = damp(lunge, state.mode === 'attack' ? 1 : 0, 7, dt);
      dead = damp(dead, state.mode === 'dead' ? 1 : 0, 2.2, dt);
      const alive = 1 - dead;
      // The tempo does not care what the legs are doing, so the nod and the
      // step drift in and out of phase forever and never lock.
      beat += dt * TEMPO * TAU * alive;
      const bob = Math.sin(beat);

      const swing = 0.14 + drive * 0.72;
      for (let i = 0; i < 2; i++) {
        const p = stride + i * Math.PI;
        const leg = rig.legs[i]!;
        // Too much knee, and too much lift, for the distance actually covered.
        leg[0]!.rotation.x = Math.sin(p) * swing * alive;
        leg[1]!.rotation.x = -Math.max(0, Math.sin(p + 0.7)) * swing * 1.35 * alive - 0.06;
      }

      // A wave down four even bones with no elbow in them. The arms are always
      // moving and they are always half a beat behind the shoulders.
      for (let i = 0; i < 2; i++) {
        const arm = rig.arms[i]!;
        const side = i === 0 ? -1 : 1;
        for (let j = 0; j < ARM_BONES; j++) {
          const p = beat - j * 0.85 + i * 0.6;
          const amp = (0.12 + drive * 0.2) * (1 - j * 0.12) * alive;
          arm[j]!.rotation.x = Math.sin(p) * amp * 1.4 + (j === 0 ? -lunge * 1.5 : lunge * 0.25);
          arm[j]!.rotation.z = side * (0.16 + Math.cos(p) * amp) * (j === 0 ? 1 : 0.55);
        }
      }

      // Bounce off the beat rather than off the step, so it is still bobbing
      // when it is standing perfectly still in a doorway.
      rig.root.position.y = (Math.abs(bob) * 0.05 + Math.abs(Math.sin(stride)) * 0.03 * drive) * alive - dead * 0.24;
      rig.root.rotation.z = dead * 1.45;
      rig.hips.rotation.y = bob * 0.12 * alive;
      rig.torso.rotation.x = 0.04 + drive * 0.2 + lunge * 0.2 - bob * 0.05;
      rig.torso.rotation.z = -bob * 0.07 * alive;
      rig.torso.rotation.y = -rig.hips.rotation.y * 1.6;

      // It nods along, and it keeps its face on the player while it does it.
      const toPlayer = Math.atan2(camera.position.x - state.pos.x, -(camera.position.z - state.pos.z));
      const want =
        state.awareness > 0.2 ? clamp(angleDelta(yaw, toPlayer), -1.3, 1.3) : Math.sin(beat * 0.31) * 0.5;
      headYaw = dampAngle(headYaw, want, 4, dt);
      rig.head.rotation.y = headYaw - rig.torso.rotation.y;
      rig.head.rotation.x = -0.06 + Math.sin(beat * 2) * 0.09 * alive;
      rig.head.rotation.z = bob * 0.06 * alive;
    },
    dispose() {
      object.clear();
      partygoerKit.release();
    },
  };
}
